import type { SttClient } from './press-to-talk.js';

/**
 * The real speech-to-text path: the recording goes to Bilal\'s own API, which forwards it to the
 * provider over the documented plain-HTTP endpoint. No provider credential ever reaches the browser,
 * and no websocket is involved — one request, one transcript, into the preview for correction.
 */
export function serverSttClient(options: {
  readonly baseUrl: string;
  readonly fetchImpl: (input: string, init?: RequestInit) => Promise<Response>;
  /** The session the recording belongs to, read at call time: capture outlives sign-in. */
  readonly sessionId: () => string | null;
}): SttClient {
  return {
    async transcribe({ audio }) {
      const sessionId = options.sessionId();
      if (sessionId === null) return { kind: 'unavailable', reason: 'provider_unavailable' };
      try {
        const response = await options.fetchImpl(
          `${options.baseUrl}/v1/sessions/${sessionId}/stt`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/octet-stream' },
            body: audio,
          },
        );
        if (response.status === 422) return { kind: 'unavailable', reason: 'unintelligible' };
        if (response.status === 429) return { kind: 'unavailable', reason: 'rate_limited' };
        if (!response.ok) return { kind: 'unavailable', reason: 'provider_unavailable' };
        const body: unknown = await response.json();
        const text = typeof body === 'object' && body !== null && 'text' in body ? body.text : null;
        if (typeof text !== 'string' || text.trim().length === 0)
          return { kind: 'unavailable', reason: 'unintelligible' };
        const confidence =
          typeof body === 'object' && body !== null && 'confidence' in body ? body.confidence : 1;
        return {
          kind: 'transcribed',
          text,
          confidence: typeof confidence === 'number' ? confidence : 1,
        };
      } catch {
        return { kind: 'unavailable', reason: 'provider_unavailable' };
      }
    },
  };
}

/** Read by the STT client at call time and written by the App when a session starts. */
export const currentSession: { id: string | null } = { id: null };
